import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { BehaviorSubject, EMPTY, Observable, of, Subject } from 'rxjs';
import { ContentTypes } from 'src/app/shared/enums/content-types';
import { FORM_FIELDS } from '../constants/form-fields';
import { FORM_PATHS } from '../constants/form-paths';
import {
  FormArray,
  FormBuilder,
  FormControl,
  Validators,
} from '@angular/forms';
import { DeliveryTargetsService } from 'src/app/content-list-management/shared/services/cmsapi/delivery-targets.service';
import { AuthorService } from 'src/app/shared/services/author.service';
import { DialogService } from 'src/app/shared/services/dialog.service';
import { WithAuthorsFormSection } from 'src/app/shared/mixins/with-authors-form-section';
import { WithContentBodyFormSection } from 'src/app/shared/mixins/with-content-body-form-section';
import { WithDeactivateGuard } from 'src/app/shared/mixins/with-deactivate-guard';
import { WithLanguagesFormSection } from 'src/app/shared/mixins/with-languages-form-section';
import { WithNextStateFormSection } from 'src/app/shared/mixins/with-next-states-form-section';
import { WithProcessing } from 'src/app/shared/mixins/with-processing';
import { WithPublishTimeFormSection } from 'src/app/shared/mixins/with-publish-time';
import { WithTaggingFormSection } from 'src/app/shared/mixins/with-tagging-form-section';
import { WithContentVisibilityFormSection } from 'src/app/shared/mixins/with-visibility-section';
import { WithWebsitesFormSection } from 'src/app/shared/mixins/with-websites-form-section';
import { IContentSetContentDto } from 'src/app/core/dtos/interfaces/i-content-set-content-dto';
import { ContentSetContentTypeService } from 'src/app/content/services/content-set-content-type.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { IImgMediaDto } from 'src/app/core/dtos/interfaces/i-img-media-dto';
import { IVideoMediaDto } from 'src/app/core/dtos/interfaces/i-video-media-dto';
import { UserSettingsService } from 'src/app/shared/services/user-settings.service';
import { LanguageService } from 'src/app/content-management/shared/services/language.service';
import { ContentSetContentDto } from 'src/app/core/dtos/content-set-content-dto';
import { catchError, filter, finalize, switchMap, tap } from 'rxjs/operators';
import { ContentTypeRouterPaths } from 'src/app/shared/enums/content-type-router-paths';
import { IAuthor } from 'src/app/core/models/i-author';
import { ITopic } from 'src/app/core/models/i-topic';
import { ITag } from 'src/app/core/models/i-tag';
import { IAllowableState } from 'src/app/core/dtos/interfaces/i-allowable-state';
import { IWebsiteUrl } from 'src/app/core/dtos/interfaces/i-website-url';
import { ContentStatus } from 'src/app/shared/enums/content-status';
import { ISaveContentResponseDto } from 'src/app/core/dtos/interfaces/i-save-content-response-dto';
import { INewContentVersionResponseDto } from 'src/app/core/dtos/interfaces/i-new-content-version-response-dto';
import { WithContentSetHeadlineFormSection } from 'src/app/shared/mixins/with-content-set-headline-form-section';
import { WithStraplineFormSection } from 'src/app/shared/mixins/with-strapline-form-section';
import { WithBannerImageFormSection } from 'src/app/shared/mixins/with-banner-image-section';
import { SharedContentTypeService } from 'src/app/content/services/shared-content-type.service';
import { IContentStateChangeResponseDto } from 'src/app/core/dtos/interfaces/i-content-state-change-response-dto';
import { WithChaptersFormSection } from 'src/app/shared/mixins/with-chapters-form-section';
import { WithSponsorLogosFormSection } from 'src/app/shared/mixins/with-sponsor-logos-form-section';
import { WithLandingPageHeadlineFormSection } from 'src/app/shared/mixins/with-landing-lage-headline';
import { WithPrimaryColourFormSection } from 'src/app/shared/mixins/with-colours-form-section';
import { PRIMARY_COLOR } from '../constants/primary-colour';
import { UrlService } from 'src/app/shared/services/url.service';
import { WithHeadlineFormSection } from 'src/app/shared/mixins/with-headline-form-section';
import { WithMainVideoImageFormSection } from 'src/app/shared/mixins/with-main-video-image-section';
import { SignalrService } from 'src/app/content-management/shared/services/signalr.service';
import { WithSurtitlesFormSection } from 'src/app/shared/mixins/with-surtitles-form-section';
import { ITypedSelectListItem } from 'src/app/shared-form-sections/interfaces/i-typed-select-list-item';
import { ISurtitle } from 'src/app/core/models/i-surtitle';
import { TopicService } from 'src/app/content-management/shared/services/topic.service';
import {
  handleTagAddedAsSurtitle,
  handleTagRemoved,
  handleTagSelected,
} from 'src/app/shared/utils/tagging-handler';
import { IChapter } from 'src/app/core/models/i-chapter';

class ContentSetContentFormComponentBase {
  FORM_PATHS = FORM_PATHS;
  FORM_FIELDS = FORM_FIELDS;
  CONTENT_TYPES = ContentTypes;
  destroy$ = new Subject<boolean>();
  form = this.formBuilder.group({});

  constructor(
    public languageService: LanguageService,
    public deliveryTargetsService: DeliveryTargetsService,
    public formBuilder: FormBuilder,
    public _dialogService: DialogService,
    public authorService: AuthorService,
    public signalRService: SignalrService,
    public topicService: TopicService
  ) {}
}

const ContentSetContentFormComponentMixinBase = WithProcessing(
  WithDeactivateGuard(
    WithHeadlineFormSection(
      WithLandingPageHeadlineFormSection(
        WithContentSetHeadlineFormSection(
          WithStraplineFormSection(
            WithBannerImageFormSection(
              WithMainVideoImageFormSection(
                WithContentBodyFormSection(
                  WithNextStateFormSection(
                    WithWebsitesFormSection(
                      WithLanguagesFormSection(
                        WithAuthorsFormSection(
                          WithPublishTimeFormSection(
                            WithTaggingFormSection(
                              WithSurtitlesFormSection(
                                WithContentVisibilityFormSection(
                                  WithChaptersFormSection(
                                    WithSponsorLogosFormSection(
                                      WithPrimaryColourFormSection(
                                        ContentSetContentFormComponentBase
                                      )
                                    )
                                  )
                                )
                              )
                            )
                          )
                        )
                      )
                    )
                  )
                )
              )
            )
          )
        )
      )
    )
  )
);

@Component({
  selector: 'app-content-set-content-form',
  templateUrl: './content-set-content-form.component.html',
  styleUrls: ['./content-set-content-form.component.scss'],
})
export class ContentSetContentFormComponent
  extends ContentSetContentFormComponentMixinBase
  implements OnInit, OnDestroy
{
  FORM_FIELDS = FORM_FIELDS;
  FORM_PATHS = FORM_PATHS;
  CONTENT_TYPES = ContentTypes;
  destroy$ = new Subject<boolean>();
  formReady = new BehaviorSubject<boolean>(false);

  isSaving = false;
  isProcessingStateChange = false;
  isEditMode = false;
  isNewContentSet = false;

  id: string;
  pageHeader: string;
  contentSetContent$!: Observable<IContentSetContentDto>;
  initialContentSetContent!: IContentSetContentDto;
  currentUserName: string = '';

  form = this.formBuilder.group({
    [this.FORM_FIELDS.LANDING_PAGE_HEADLINE]: [''],
    [this.FORM_FIELDS.HEADLINE]: ['', Validators.required],
    [this.FORM_FIELDS.CONTENT_SET_HEADLINE]: ['', Validators.required],
    [this.FORM_FIELDS.STRAPLINE]: ['', Validators.required],
    [this.FORM_FIELDS.LANGUAGE]: [null],
    [this.FORM_FIELDS.BODY]: [],
    [this.FORM_FIELDS.IMGS]: this.formBuilder.array([]),
    [this.FORM_FIELDS.VIDEOS]: this.formBuilder.array([]),
    [this.FORM_FIELDS.WEBSITES]: this.formBuilder.group(
      {},
      {
        validators: [this._initWebsiteValidation()],
      }
    ),
    [this.FORM_FIELDS.INTERNAL_INFORMATION]: this.formBuilder.group({
      [this.FORM_FIELDS.LIVE_URLS]: this.formBuilder.array([]),
      [this.FORM_FIELDS.CONTENT_IDS]: [],
    }),
    [this.FORM_FIELDS.ARTICLE_VERSION_LIST]: this.formBuilder.array([]),
    [this.FORM_FIELDS.NEXT_STATES]: [],
    [this.FORM_FIELDS.PUBLISH]: this.formBuilder.group(
      {
        [this.FORM_FIELDS.DATE]: [],
        [this.FORM_FIELDS.TIMEZONE]: [],
        [this.FORM_FIELDS.SHOW_PUBLISH_TIME]: [false],
      },
      {
        validators: [this._initPublishTimeValidation()],
      }
    ),
    [this.FORM_FIELDS.SURTITLES]: this.formBuilder.array([]),
    [this.FORM_FIELDS.EDITION_LANGUAGE]: ['en'],
    [this.FORM_FIELDS.TYPED_TAGS]: this.formBuilder.array([]),
    [this.FORM_FIELDS.TAGS]: this.formBuilder.array([]),
    [this.FORM_FIELDS.TOPICS]: this.formBuilder.array([]),
    [this.FORM_FIELDS.TOPICS_WITH_TRANSLATIONS]: this.formBuilder.array([]),
    [this.FORM_FIELDS.COMPANIES]: this.formBuilder.array([]),
    [this.FORM_FIELDS.PEOPLE]: this.formBuilder.array([]),
    [this.FORM_FIELDS.SELECTED_AUTHORS]: this.formBuilder.array([]),
    [this.FORM_FIELDS.RESTRICTED_TO_REGISTERED_USERS]: [null],
    [this.FORM_FIELDS.NOT_FOR_GOOGLE_NEWS_SITEMAPS]: [null],
    [this.FORM_FIELDS.NOT_FOR_NEWS_FEED]: [null],
    [this.FORM_FIELDS.HIDE_THIS_ARTICLE]: [null],
    [this.FORM_FIELDS.PAYWALL_PREMIUM_CONTENT]: [null],
    [this.FORM_FIELDS.NOT_FOR_COMPETITORS]: [null],
    [this.FORM_FIELDS.BANNER_IMAGE]: this.formBuilder.array<IImgMediaDto[]>([]),
    [this.FORM_FIELDS.CHAPTERS]: this.formBuilder.array<IChapter[]>([]),
    [this.FORM_FIELDS.SPONSOR_LOGOS]: this.formBuilder.array<IImgMediaDto[]>(
      []
    ),
    [this.FORM_FIELDS.PRIMARY_COLOUR_HEX]: this.formBuilder.control<string>(''),
    [this.FORM_FIELDS.MAIN_IMAGE]: this.formBuilder.array<IImgMediaDto[]>([]),
    [this.FORM_FIELDS.DISPLAY_IMAGE_ON_WEBSITE]:
      this.formBuilder.control<boolean>(false),
  });

  readonly getContentFn$ = (id: string): Observable<IContentSetContentDto> => {
    return this.getContentSetContent$(id);
  };

  readonly createNewVersionFn$ = (
    contentId: string,
    versionId: string
  ): Observable<INewContentVersionResponseDto> => {
    return this.contentSetService.createVersion$(contentId, versionId);
  };

  readonly deleteVersionFn$ = (
    contentId: string,
    versionId: string
  ): Observable<Object> => {
    return this.contentSetService.deleteVersion$(contentId, versionId);
  };

  @HostListener('window:beforeunload', ['$event'])
  beforeUnload(event: Event): void {
    this.handleBeforeUnload(event);
  }

  constructor(
    public formBuilder: FormBuilder,
    public languageService: LanguageService,
    public deliveryTargetsService: DeliveryTargetsService,
    public contentSetService: ContentSetContentTypeService,
    public sharedService: SharedContentTypeService,
    public urlService: UrlService,
    public _dialogService: DialogService,
    private _activatedRoute: ActivatedRoute,
    private _snackBar: MatSnackBar,
    private _router: Router,
    private _userSettingsService: UserSettingsService,
    public authorService: AuthorService,
    public signalRService: SignalrService,
    public topicService: TopicService
  ) {
    super(
      languageService,
      deliveryTargetsService,
      formBuilder,
      _dialogService,
      authorService,
      signalRService,
      topicService
    );
  }

  ngOnInit(): void {
    this._routerUrlSub();

    if (this.isNewContentSet) {
      this.pageHeader = 'Create Content Set';
      this.initialContentSetContent = new ContentSetContentDto({});
      this.initialContentSetContent.dynamicData.allowableNextStates = [];
      this.initialContentSetContent.dynamicData.versions = [];
      this.initialContentSetContent.dynamicData.liveUrls = [];
      this.initialContentSetContent.dynamicData.previewUrls = [];
      this.initialContentSetContent.primaryColourHex = PRIMARY_COLOR;

      this._userSettingsService.getForCurrentUser$().subscribe((x) => {
        this.initialContentSetContent.authors = x.defaultArticleAuthors ?? [];
        this.initialContentSetContent.languageCode = x.defaultLanguageCode;
        this.initialContentSetContent.deliveryTargetCodes =
          x.defaultDeliveryTargetCodes ?? [];
        this._initForm();
      });
    } else {
      this.pageHeader = 'Edit Content Set';
      this._routeParamsSub();
    }

    if (!this.isEditMode && !this.isNewContentSet) {
      this.form.disable();
    }

    this._userSettingsService.getCurrentUserName$().subscribe((username) => {
      this.currentUserName = username;
    });
  }

  private _routerUrlSub(): void {
    this._activatedRoute.url.subscribe((path) => {
      const segments = path.map((segment) => segment.path);
      this.isEditMode =
        segments.length > 0 &&
        (segments.includes('edit') || segments.includes('new'));

      this.isNewContentSet = segments.length > 0 && segments.includes('new');
    });
  }

  private _routeParamsSub(): void {
    this._activatedRoute.paramMap
      .pipe(
        filter((paramMaps) => {
          return !!paramMaps.get('id');
        }),
        switchMap((paramsMap) => {
          this.id = paramsMap.get('id');
          return this.process$(this.getContentSetContent$(this.id));
        })
      )
      .pipe(
        catchError((error) => {
          if (error.status === 404) {
            this._dialogService.openErrorDialogAndNavigate(
              {
                title: 'Content set not found',
                message:
                  'The content set was not found. It may have been deleted.',
              },
              'dashboard'
            );
          } else {
            this._dialogService.openErrorDialogAndNavigate(
              {
                message:
                  'An error has occurred loading the content set. Please contact IT.',
              },
              'dashboard'
            );
          }

          return EMPTY;
        })
      )
      .subscribe({
        next: (contentSetContentDto: IContentSetContentDto) => {
          this.initialContentSetContent = contentSetContentDto;

          this.setEditMode(contentSetContentDto.dynamicData.canEdit);
          this._initForm();
          this._findEditionLanguage(this.getCurrentWebsites());
        },
      });
  }

  private setEditMode(newEditModeValue) {
    if (newEditModeValue !== this.isEditMode) {
      newEditModeValue
        ? this.redirectToEditContent(this.id)
        : this.redirectToReadOnlyContent(this.id);
      return;
    }
    this.isEditMode = newEditModeValue;

    this.isEditMode ? this.form.enable() : this.form.disable();
  }

  private redirectToReadOnlyContent(id: string) {
    this._router.navigate([
      '/content',
      ContentTypeRouterPaths[ContentTypes.CONTENT_SET],
      id,
    ]);
  }

  private redirectToEditContent(id: string) {
    this._router.navigate([
      '/content',
      ContentTypeRouterPaths[ContentTypes.CONTENT_SET],
      id,
      'edit',
    ]);
  }

  private _initForm(): void {
    this._initDeliveryTargets(this.getCurrentWebsites, this.getCurrentUrls);
    this._initHeadline(this.initialContentSetContent.headline);
    this._initLandingPageHeadline(
      this.initialContentSetContent.landingPageHeadline
    );
    this._initContentSetHeadline(
      this.initialContentSetContent.contentSetHeadline
    );
    this._initStrapline(this.initialContentSetContent.strapline);
    this._initBannerImage(this.initialContentSetContent.bannerImage);
    this._initMainVideoImage(
      this.initialContentSetContent.mainImage,
      null,
      this.initialContentSetContent.displayImageOnWebsite
    );
    this._initLanguages(this.getCurrentSelectedLanguage);
    this._initNextStates(
      this.initialContentSetContent.dynamicData.allowableNextStates
    );
    this._initContentBody(
      this.initialContentSetContent.body,
      this.initialContentSetContent.bodyImages,
      this.initialContentSetContent.bodyVideos
    );
    this._initAuthors(this.initialContentSetContent.authors);
    this._initTagging(
      this.initialContentSetContent.tags,
      this.initialContentSetContent.topics,
      [],
      [],
      this.topicService
    );
    this._initSurtitles(
      this.initialContentSetContent.surtitlesDetailed,
      this.initialContentSetContent.tags,
      this.initialContentSetContent.topics,
      [],
      []
    );
    this._initPublishTime(
      this.initialContentSetContent.presetPublishDate,
      this.initialContentSetContent.timezoneIdentifier
    );

    this._initContentVisibility(
      this.initialContentSetContent.restrictToRegisteredUsers,
      this.initialContentSetContent.notForGoogleNewsSitemaps,
      this.initialContentSetContent.notForNewsFeeds,
      this.initialContentSetContent.hideThisArticle,
      this.initialContentSetContent.paywallPremiumContent,
      this.initialContentSetContent.notForCompetitors
    );
    this._initChapters(this.initialContentSetContent.chapters);

    this._initSponsorLogos(this.initialContentSetContent.sponsorLogos);
    this._initPrimaryColour(this.initialContentSetContent.primaryColourHex);

    this._initFormValueChanges();
    this.formReady.next(true);
  }

  private _isVersionId(contentIdToCheck: string): boolean {
    return contentIdToCheck.includes('ContentSet');
  }

  private _initFormValueChanges(): void {
    this._initHeadlineValueChanges(this.getCurrentHeadline);
    this._initLanguageValueChanges(this.getCurrentSelectedLanguage);
    this._initWebsitesValueChanges(this.getCurrentWebsites);
    this._initLandingPageHeadlineValueChanges(this.getLandingPageHeadline);
    this._initContentSetHeadlineValueChanges(this.getContentSetHeadline);
    this._initStraplineValueChanges(this.getStrapline);
    this._initBannerImageValueChanges(this.getBannerImage);
    this._initMainImageVideoValueChanges(
      this.getMainImage,
      null,
      this.getDisplayImageOnWebsite
    );
    this._initContentBodyValueChanges(
      this.getBody,
      this.getBodyImgs,
      this.getBodyVideos
    );
    this._initNextStatesValueChanges(this.getNextStates);
    this._initPublishTimeValueChanges(
      this.getPresetPublishTime,
      this.getTimezone
    );
    this._initTaggingValueChanges(
      this.getTags,
      this.getTopics,
      () => [],
      () => []
    );
    this._initSurtitlesValueChanges(this.getSurtitles);
    this._initContentVisibilityValueChanges(
      this.getRestrictToRegisteredUsers,
      this.getNotForGoogleNewsSitemaps,
      this.getNotForNewsFeeds,
      this.getHideThisArticle,
      this.getPaywallPremiumContent,
      this.getNotForCompetitors
    );
    this._initSelectedAuthorsValueChanges(this.getSelectedAuthors);
    this._initChaptersValueChanges(this.getChapters);

    this._initSponsorLogosValueChanges(this.getSponsorLogos);
    this._initPrimaryColourValueChanges(this.getPrimaryColour);
  }

  private _updateForms(): void {
    this._updateDeliveryTargets(this.getCurrentWebsites, this.getCurrentUrls);
  }

  getCurrentSelectedLanguage = () => {
    return this.initialContentSetContent.languageCode;
  };

  getCurrentWebsites = (): string[] | null => {
    return this.initialContentSetContent.deliveryTargetCodes;
  };

  getCurrentUrls = (): IWebsiteUrl[] | null => {
    if (this.initialContentSetContent.status != ContentStatus.PUBLISHED) {
      return this.initialContentSetContent.deliveryTargetCodes.map(
        (targetCode) => {
          return {
            deliveryTargetCode: targetCode,
            url: this.urlService.getPreviewUrl(
              this.initialContentSetContent.id,
              targetCode
            ),
          };
        }
      );
    }
    return this.initialContentSetContent.dynamicData.liveUrls;
  };

  getCurrentHeadline = (): string | null => {
    return this.initialContentSetContent.headline;
  };

  getLandingPageHeadline = (): string | null => {
    return this.initialContentSetContent.landingPageHeadline;
  };

  getContentSetHeadline = (): string | null => {
    return this.initialContentSetContent.contentSetHeadline;
  };

  getStrapline = (): string | null => {
    return this.initialContentSetContent.strapline;
  };

  getBannerImage = (): IImgMediaDto | null => {
    return this.initialContentSetContent.bannerImage;
  };

  getMainImage = (): IImgMediaDto | null => {
    return this.initialContentSetContent.mainImage;
  };

  getDisplayImageOnWebsite = (): boolean => {
    return this.initialContentSetContent.displayImageOnWebsite;
  };

  getBody = (): string | null => {
    return this.initialContentSetContent.body;
  };

  getBodyImgs = (): IImgMediaDto[] | null => {
    return this.initialContentSetContent.bodyImages;
  };

  getBodyVideos = (): IVideoMediaDto[] | null => {
    return this.initialContentSetContent.bodyVideos;
  };

  getNextStates = (): IAllowableState[] | null => {
    return this.initialContentSetContent.dynamicData.allowableNextStates;
  };
  getPresetPublishTime = (): Date | null => {
    return this.initialContentSetContent.presetPublishDate;
  };

  getTimezone = (): string | null => {
    return this.initialContentSetContent.timezoneIdentifier;
  };
  getSurtitles = (): ISurtitle[] | null => {
    return this.initialContentSetContent.surtitlesDetailed;
  };

  getTags = (): ITag[] | null => {
    return this.initialContentSetContent.tags;
  };

  getTopics = (): ITopic[] | null => {
    return this.initialContentSetContent.topics;
  };

  getRestrictToRegisteredUsers = (): boolean | null => {
    return this.initialContentSetContent.restrictToRegisteredUsers;
  };

  getNotForGoogleNewsSitemaps = (): boolean | null => {
    return this.initialContentSetContent.notForGoogleNewsSitemaps;
  };

  getNotForNewsFeeds = (): boolean | null => {
    return this.initialContentSetContent.notForNewsFeeds;
  };

  getHideThisArticle = (): boolean | null => {
    return this.initialContentSetContent.hideThisArticle;
  };

  getPaywallPremiumContent = (): boolean | null => {
    return this.initialContentSetContent.paywallPremiumContent;
  };
  getNotForCompetitors = (): boolean | null => {
    return this.initialContentSetContent.notForCompetitors;
  };

  getSelectedAuthors = (): IAuthor[] | null => {
    return this.initialContentSetContent.authors;
  };
  getChapters = (): IChapter[] | null => {
    return this.initialContentSetContent.chapters;
  };

  getSponsorLogos = (): IImgMediaDto[] | null => {
    return this.initialContentSetContent.sponsorLogos;
  };

  getPrimaryColour = (): string | null => {
    return this.initialContentSetContent.primaryColourHex;
  };

  onCreateNewVersion(): void {
    this.isProcessingStateChange = true;

    this.sharedService
      .createNewVersionWithCurrentStateCheck$(
        this.createNewVersionFn$,
        this.deleteVersionFn$,
        this.initialContentSetContent.contentId,
        this.initialContentSetContent.id,
        this.initialContentSetContent?.dynamicData?.currentDraftVersion
      )
      .pipe(
        finalize(() => {
          this.isProcessingStateChange = false;
        })
      )
      .subscribe(() => {
        this._snackBar.open(
          `New content set version created successfully`,
          'Save',
          {
            duration: 5 * 1000,
          }
        );
        this.redirectToEditContent(this.initialContentSetContent.contentId);
      });
  }

  onUpdateState(stateAction: ContentStatus): void {
    this.isProcessingStateChange = true;

    const updateStateFn$ = (
      versionId: string,
      action: string
    ): Observable<IContentStateChangeResponseDto> => {
      return this.contentSetService.updateState$(versionId, action);
    };
    const setEditModeFn = (newEditModeValue: boolean) => {
      this.setEditMode(newEditModeValue);
    };

    this.sharedService
      .updateState<IContentSetContentDto>(
        updateStateFn$,
        this.getContentFn$,
        setEditModeFn,
        this.initialContentSetContent.id,
        stateAction,
        this.isEditMode
      )
      .pipe(
        tap((refreshedDto: IContentSetContentDto) => {
          this.initialContentSetContent = refreshedDto;
        }),
        finalize(() => {
          this.isProcessingStateChange = false;
        })
      )
      .subscribe();
  }

  onSave(): void {
    if (this.isNewContentSet) {
      this.isSaving = true;
      const createFn$ = (
        content: IContentSetContentDto
      ): Observable<ISaveContentResponseDto> => {
        return this.contentSetService.create$(content);
      };
      this.sharedService
        .createNewContent$<IContentSetContentDto>(
          createFn$,
          this.buildContentSet()
        )
        .pipe(finalize(() => (this.isSaving = false)))
        .subscribe((x: ISaveContentResponseDto) => {
          this.form.markAsPristine();
          this._router.navigate([
            '/content',
            ContentTypeRouterPaths[ContentTypes.CONTENT_SET],
            x.contentId,
            'edit',
          ]);
        });
    } else {
      const updateFn$ = (contentId: string, content: IContentSetContentDto) => {
        return this.contentSetService.update$(contentId, content);
      };

      this.isSaving = true;
      this.sharedService
        .saveContent<IContentSetContentDto>(
          updateFn$,
          this.getContentFn$,
          null,
          this.getCurrentWebsites(),
          this._buildDeliveryTargetCodes(),
          this.deliveryTargets$,
          this.buildContentSet()
        )
        .pipe(finalize(() => (this.isSaving = false)))
        .subscribe((refreshedDto: IContentSetContentDto) => {
          this._snackBar.open('Content set saved successfully', null, {
            duration: 5 * 1000,
          });
          this.initialContentSetContent = refreshedDto;
          this.form.markAsPristine();
          this._updateForms();
          this.signalRService.userSavedPage(
            this.currentUserName,
            refreshedDto.contentId
          );
        });
    }
  }

  deleteContentSet() {
    const deleteFn$ = (contentId: string) => {
      return this.contentSetService.delete$(contentId);
    };

    this.sharedService
      .deleteContent$(deleteFn$, this.initialContentSetContent.contentId)
      .subscribe({
        next: () => {
          this._router.navigate(['/dashboard']);
        },
      });
  }

  spikeContentSet() {
    const spikeFn$ = (contentId: string) => {
      return this.contentSetService.spike$(contentId);
    };
    this.sharedService
      .spikeContent$(spikeFn$, this.initialContentSetContent.contentId)
      .subscribe({
        next: () => {
          this._router.navigate(['/dashboard']);
        },
      });
  }

  deleteUnpublishedDraft() {
    this.sharedService
      .deleteUnpublishedDraft$(
        this.deleteVersionFn$,
        this.initialContentSetContent.contentId,
        this.initialContentSetContent.id
      )
      .subscribe({
        next: () => {
          this._router.navigate(['/dashboard']);
        },
      });
  }

  onReactivateContentSet(): void {
    this.isProcessingStateChange = true;

    this.sharedService
      .reactivateContent(
        this.getContentFn$,
        this.createNewVersionFn$,
        this.deleteVersionFn$,
        this.initialContentSetContent.contentId,
        this.initialContentSetContent.id,
        this.initialContentSetContent.dynamicData.latestPublishedVersionId,
        this.getCurrentWebsites(),
        this.initialContentSetContent.dynamicData.currentDraftVersion,
        this.deliveryTargets$,
        this.isPreviouslyPublishedVersion()
      )
      .pipe(finalize(() => (this.isProcessingStateChange = false)))
      .subscribe({
        next: () => {
          this._snackBar.open(`Content set Reactivated successfully`, null, {
            duration: 5 * 1000,
          });
          this.redirectToEditContent(this.initialContentSetContent.contentId);
        },
      });
  }

  private isPreviouslyPublishedVersion(): boolean {
    return (
      this.initialContentSetContent.status === ContentStatus.PUBLISHED &&
      this.initialContentSetContent?.dynamicData?.latestPublishedVersionId !==
        this.initialContentSetContent?.id
    );
  }

  private getContentSetContent$(id: string) {
    if (this._isVersionId(id)) {
      this.contentSetContent$ = this.process$(
        this.contentSetService.getByVersionId$(id)
      );
    } else {
      this.contentSetContent$ = this.process$(
        this.contentSetService.getById$(id)
      );
    }

    return this.contentSetContent$;
  }

  onTagItemSelected($event) {
    const typedTags = this.form.get(FORM_PATHS.TYPED_TAGS()) as FormArray;

    handleTagSelected($event, typedTags);
  }

  onTagItemRemoved($event: ITypedSelectListItem) {
    const typedTags = this.form.get(FORM_PATHS.TYPED_TAGS()) as FormArray;
    const surtitleControl: FormArray<FormControl<ISurtitle>> = this.form.get(
      FORM_PATHS.SURTITLES()
    ) as FormArray;

    handleTagRemoved($event, typedTags, surtitleControl);
  }

  onSurtitlePromoted($event: ITypedSelectListItem) {
    const surtitleControl: FormArray<FormControl<ISurtitle>> = this.form.get(
      FORM_PATHS.SURTITLES()
    ) as FormArray;

    handleTagAddedAsSurtitle($event, surtitleControl);
  }

  onEditionAdded(): void {
    this._findEditionLanguage(this._buildDeliveryTargetCodes());
  }

  onEditionRemoved(): void {
    this._findEditionLanguage(this._buildDeliveryTargetCodes());
  }

  private buildContentSet(): ContentSetContentDto {
    const newContentSetContent = new ContentSetContentDto({});
    Object.assign(newContentSetContent, this.initialContentSetContent);
    newContentSetContent.headline = this._buildHeadline();
    newContentSetContent.landingPageHeadline = this._buildLandingPageHeadline();
    newContentSetContent.contentSetHeadline = this._buildContentSetHeadline();
    newContentSetContent.strapline = this._buildStrapline();
    newContentSetContent.bannerImage = this._buildBannerImage();
    newContentSetContent.body = this._buildBody();
    newContentSetContent.bodyImages = this._buildBodyImages();
    newContentSetContent.bodyVideos = this._buildBodyVideos();
    newContentSetContent.deliveryTargetCodes = this._buildDeliveryTargetCodes();
    newContentSetContent.languageCode = this._buildLanguage();
    newContentSetContent.surtitlesDetailed = this._buildSurtitles();
    newContentSetContent.topics = this._buildTopics();
    newContentSetContent.tags = this._buildTags();
    newContentSetContent.authors = this._buildSelectedAuthors();
    newContentSetContent.presetPublishDate = this._buildPresetPublishTime();
    newContentSetContent.timezoneIdentifier = this._buildTimezone();
    newContentSetContent.restrictToRegisteredUsers =
      this._buildRestrictToRegisteredUsers();
    newContentSetContent.notForGoogleNewsSitemaps =
      this._buildNotForGoogleNewsSitemaps();
    newContentSetContent.notForNewsFeeds = this._buildNotForNewsFeeds();
    newContentSetContent.hideThisArticle = this._buildHideThisArticle();
    newContentSetContent.paywallPremiumContent =
      this._buildPaywallPremiumContent();
    newContentSetContent.notForCompetitors = this._buildNotForCompetitors();
    newContentSetContent.chapters = this._buildChapters();
    newContentSetContent.sponsorLogos = this._buildSponsorLogos();
    newContentSetContent.primaryColourHex = this._buildPrimaryColour();
    newContentSetContent.displayImageOnWebsite =
      this._buildDisplayImageOnWebsite();
    newContentSetContent.mainImage = this._buildMainImage();

    return newContentSetContent;
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }
}
